/**
 * Management-dashboard berekeningen — pure functies.
 *
 * Conform docs/specs/20-calculatie-offerte-tool.md §2.5 + §2.6. De API-route
 * (`apps/api/src/routes/management-dashboard.ts`) haalt de ruwe inputs uit de
 * DB (pakkettarieven, gemiddelde monteur-kostprijs, geplande uren, verlof) en
 * voedt die aan deze pure functies. Zo blijft de rekenlogica I/O-vrij en
 * unit-testbaar (geen gedeelde dev-DB nodig).
 *
 * Precisie: tussenstappen in volle precisie; afronden op 2 decimalen aan het
 * eind (zelfde conventie als quote-calculations.ts / employee-cost.ts).
 */

const round2 = (n: number): number => Math.round(n * 100) / 100
const round1 = (n: number): number => Math.round(n * 10) / 10

// ─── A. Marge per pakket (spec §2.5.1 A) ─────────────────────────────────────

export interface PackageMarginInput {
  /** Pakket-key (basis/service/ontzorg/flex). */
  key: string
  /** UI-label. */
  label: string
  /** Verkooptarief €/uur (uit Tariff SERVICE_PACKAGE). */
  sellRate: number
}

export interface PackageMarginRow {
  key: string
  label: string
  sellRate: number
  /** Gemiddelde kostprijs/uur van de ingezette monteurs. */
  avgMonteurCost: number
  /** Marge €/uur = sellRate − avgMonteurCost. */
  marginPerHour: number
  /** Marge % t.o.v. verkooptarief; 0 bij sellRate = 0. */
  marginPercentage: number
  /** True wanneer de marge negatief is (rode achtergrond in de UI). */
  negative: boolean
}

/**
 * Bereken de marge per pakket: verkooptarief − gemiddelde monteur-kostprijs.
 * `avgMonteurCost` is hetzelfde voor elk pakket (gemiddelde over alle monteurs);
 * de spec-tabel §2.5.1 toont exact dat patroon.
 */
export function computePackageMargins(
  packages: PackageMarginInput[],
  avgMonteurCost: number,
): PackageMarginRow[] {
  return packages.map((p) => {
    const marginPerHour = p.sellRate - avgMonteurCost
    const marginPercentage = p.sellRate > 0 ? (marginPerHour / p.sellRate) * 100 : 0
    return {
      key: p.key,
      label: p.label,
      sellRate: round2(p.sellRate),
      avgMonteurCost: round2(avgMonteurCost),
      marginPerHour: round2(marginPerHour),
      marginPercentage: round1(marginPercentage),
      negative: marginPerHour < 0,
    }
  })
}

// ─── B. Bezettingsgraad (spec §2.5.1 B) ──────────────────────────────────────

export interface UtilizationInput {
  employeeId: string
  name: string
  functie: string | null
  /** Geplande declarabele uren in de periode (som van planning-duur). */
  plannedHours: number
  /** Beschikbare uren in de periode (declarabele norm − verlof). */
  availableHours: number
}

export interface UtilizationRow {
  employeeId: string
  name: string
  functie: string | null
  plannedHours: number
  availableHours: number
  /** bezetting = (planned / available) × 100; 0 bij available = 0. */
  utilizationPct: number
  /** Boven/onder de target-lijn (default 85%). */
  aboveTarget: boolean
}

/**
 * Bezettingsgraad per medewerker: geplande declarabele uren / beschikbare uren.
 * `targetPct` (default 85) bepaalt de `aboveTarget`-vlag voor de target-lijn.
 */
export function computeUtilization(
  rows: UtilizationInput[],
  targetPct = 85,
): UtilizationRow[] {
  return rows.map((r) => {
    const utilizationPct = r.availableHours > 0 ? (r.plannedHours / r.availableHours) * 100 : 0
    return {
      employeeId: r.employeeId,
      name: r.name,
      functie: r.functie,
      plannedHours: round2(r.plannedHours),
      availableHours: round2(r.availableHours),
      utilizationPct: round1(utilizationPct),
      aboveTarget: utilizationPct >= targetPct,
    }
  })
}

// ─── C. Omzet/kosten/winst prognose (spec §2.5.1 C) ──────────────────────────

export interface RevenuePeriodInput {
  /** Periode-label (bv. '2026-05' of '2026-Q2'). */
  period: string
  /** Omzet = geplande/gefactureerde uren × uurtarief (+ evt. reiskosten). */
  revenue: number
  /** Kosten = ingezette uren × kostprijs/uur. */
  cost: number
}

export interface RevenueForecastRow {
  period: string
  revenue: number
  cost: number
  /** winst = omzet − kosten. */
  profit: number
  /** marge % t.o.v. omzet; 0 bij omzet = 0. */
  marginPercentage: number
}

export function computeRevenueForecast(periods: RevenuePeriodInput[]): RevenueForecastRow[] {
  return periods.map((p) => {
    const profit = p.revenue - p.cost
    const marginPercentage = p.revenue > 0 ? (profit / p.revenue) * 100 : 0
    return {
      period: p.period,
      revenue: round2(p.revenue),
      cost: round2(p.cost),
      profit: round2(profit),
      marginPercentage: round1(marginPercentage),
    }
  })
}

// ─── Capaciteit per medewerker (spec §2.6.1 + §2.6.2) ────────────────────────

export interface CapacityRowInput {
  employeeId: string
  name: string
  functie: string | null
  /** Team/afdeling-naam (Department) of null — groepeer-as `team` (spec §2.6.2). */
  team: string | null
  /** Entiteit/BV-naam of null — groepeer-as `entiteit` (spec §2.6.2). */
  entiteit: string | null
  /** Kostprijs/uur (uit EmployeeCostProfile snapshot). */
  costPerHour: number
  /** Pakket-label (of null wanneer niet bepaald). */
  pakket: string | null
  /** Ploeg-label (of null). */
  ploeg: string | null
  /** Geplande uren in de periode. */
  plannedHours: number
  /**
   * Reistijd-uren in de periode (uit werkbon-dagregels). `null` = geen
   * reisdata beschikbaar (kolom toont '—' i.p.v. een misleidende 0).
   */
  travelHours: number | null
  /** Gereden km in de periode. `null` = geen reisdata beschikbaar. */
  km: number | null
  /** Verkoop-uurtarief voor het pakket (excl. ploegtoeslag). */
  sellRate: number
  /** Ploegtoeslag-percentage (0 = geen). */
  shiftPct: number
  /** Reiskosten (km × km-tarief). */
  travelRevenue: number
  /** Autokosten pro rata in de periode (deel van autokostenJaar). */
  vehicleCostProRata: number
}

export interface CapacityRow {
  employeeId: string
  name: string
  functie: string | null
  team: string | null
  entiteit: string | null
  costPerHour: number
  pakket: string | null
  ploeg: string | null
  plannedHours: number
  /** Reistijd-uren of null wanneer geen reisdata beschikbaar (UI: '—'). */
  travelHours: number | null
  /** Gereden km of null wanneer geen reisdata beschikbaar (UI: '—'). */
  km: number | null
  /** omzet = uren × uurtarief × (1 + ploeg%) + reiskosten. */
  revenue: number
  /** kosten = uren × kostprijs/uur + autokosten pro rata. */
  cost: number
  /** winst = omzet − kosten. */
  profit: number
  marginPercentage: number
}

export function computeCapacityRow(input: CapacityRowInput): CapacityRow {
  const labor = input.plannedHours * input.sellRate * (1 + input.shiftPct / 100)
  const revenue = labor + input.travelRevenue
  const cost = input.plannedHours * input.costPerHour + input.vehicleCostProRata
  const profit = revenue - cost
  const marginPercentage = revenue > 0 ? (profit / revenue) * 100 : 0
  return {
    employeeId: input.employeeId,
    name: input.name,
    functie: input.functie,
    team: input.team,
    entiteit: input.entiteit,
    costPerHour: round2(input.costPerHour),
    pakket: input.pakket,
    ploeg: input.ploeg,
    plannedHours: round2(input.plannedHours),
    travelHours: input.travelHours == null ? null : round2(input.travelHours),
    km: input.km == null ? null : round2(input.km),
    revenue: round2(revenue),
    cost: round2(cost),
    profit: round2(profit),
    marginPercentage: round1(marginPercentage),
  }
}

export interface CapacityTotals {
  totalRevenue: number
  totalCost: number
  totalProfit: number
  avgMarginPercentage: number
}

/** Totaalrij (spec §2.6.2): som van omzet/kosten/winst + gemiddelde marge %. */
export function computeCapacityTotals(rows: CapacityRow[]): CapacityTotals {
  const totalRevenue = rows.reduce((a, r) => a + r.revenue, 0)
  const totalCost = rows.reduce((a, r) => a + r.cost, 0)
  const totalProfit = totalRevenue - totalCost
  const avgMarginPercentage = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0
  return {
    totalRevenue: round2(totalRevenue),
    totalCost: round2(totalCost),
    totalProfit: round2(totalProfit),
    avgMarginPercentage: round1(avgMarginPercentage),
  }
}

/** Gemiddelde van een lijst getallen (0 bij lege lijst). 2-decimalen afgerond. */
export function average(values: number[]): number {
  if (values.length === 0) return 0
  return round2(values.reduce((a, b) => a + b, 0) / values.length)
}

// ─── Ploeg-afleiding per medewerker (spec §2.6.1) ────────────────────────────

/**
 * ShiftType-keys zoals in de Prisma-enum (DAG/PLOEGEN_2..5). String-typed zodat
 * deze pure functie geen Prisma-import nodig heeft; de route levert de echte
 * `snapshotShiftType`-waarden uit de werkbon-dagregels van de medewerker aan.
 */
export type ShiftKey = 'DAG' | 'PLOEGEN_2' | 'PLOEGEN_3' | 'PLOEGEN_4' | 'PLOEGEN_5'

/** Nederlandse UI-labels voor de ploeg-kolom in de capaciteitstabel. */
export const SHIFT_LABELS: Record<ShiftKey, string> = {
  DAG: 'Dag',
  PLOEGEN_2: '2-ploegen',
  PLOEGEN_3: '3-ploegen',
  PLOEGEN_4: '4-ploegen',
  PLOEGEN_5: '5-ploegen',
}

/**
 * Bepaal de representatieve ploeg van een medewerker uit zijn werkelijk gewerkte
 * shifts in de periode (modus: meest voorkomende `snapshotShiftType` over de
 * werkbon-dagregels). Géén hardcoded 'Dag': bij ontbrekende shift-data geeft de
 * functie `null` terug (UI toont '—'), zodat we geen ploeg suggereren die er
 * niet is. Bij gelijke stand wint de zwaarste ploeg (meeste toeslag), zodat
 * een capaciteits-prognose niet structureel te laag uitvalt.
 */
export function deriveRepresentativeShift(shifts: Array<ShiftKey | null | undefined>): ShiftKey | null {
  const counts = new Map<ShiftKey, number>()
  for (const s of shifts) {
    if (s == null) continue
    counts.set(s, (counts.get(s) ?? 0) + 1)
  }
  if (counts.size === 0) return null
  // Volgorde van "zwaarte" voor de tie-break (laatste = zwaarst).
  const weight: ShiftKey[] = ['DAG', 'PLOEGEN_2', 'PLOEGEN_3', 'PLOEGEN_4', 'PLOEGEN_5']
  let best: ShiftKey | null = null
  let bestCount = -1
  for (const [shift, count] of counts) {
    if (
      count > bestCount ||
      (count === bestCount && best != null && weight.indexOf(shift) > weight.indexOf(best))
    ) {
      best = shift
      bestCount = count
    }
  }
  return best
}
